import { NavLink } from "react-router-dom";
import { ReactNode, useState } from "react";
import { Button } from "@xpense/ui";
import { Icon, type IconName } from "./Icons";
import AssistantDock from "./AssistantDock";

interface LayoutProps {
  children: ReactNode;
  onLogout: () => void;
}

const navItems: Array<{ to: string; label: string; icon: IconName }> = [
  { to: "/", label: "Dashboard", icon: "dashboard" },
  { to: "/transactions", label: "Transactions", icon: "transactions" },
  { to: "/review", label: "Review", icon: "review" },
  { to: "/forecast", label: "Forecast", icon: "forecast" },
  { to: "/nutrition", label: "Nutrition", icon: "activity" },
  { to: "/agents", label: "Agents", icon: "agents" },
  { to: "/connected-accounts", label: "Accounts", icon: "mail" },
  { to: "/settings", label: "Settings", icon: "settings" },
  { to: "/export", label: "Export", icon: "export" },
];

export default function Layout({ children, onLogout }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = () => setSidebarOpen(false);

  return (
    <div className="xpn-app-shell">
      <aside className={`xpn-sidebar ${sidebarOpen ? "xpn-sidebar--open" : ""}`}>
        <div className="xpn-brand">
          <div className="xpn-brand-mark">X</div>
          <div>
            <div className="xpn-brand-title">Xpense AI</div>
            <div className="xpn-brand-subtitle">Expense intelligence</div>
          </div>
        </div>

        <nav className="xpn-sidebar-nav" aria-label="Main navigation">
          {navItems.map(({ to, label, icon }) => (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={closeSidebar}
              className={({ isActive }) =>
                `xpn-sidebar-link ${isActive ? "xpn-sidebar-link--active" : ""}`
              }
            >
              <Icon name={icon} />
              <span>{label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="xpn-sidebar-footer">
          <div className="xpn-sidebar-health">
            <div className="xpn-text-micro" style={{ color: "var(--xpn-text-on-dark-muted)" }}>
              AI Pipeline
            </div>
            <div className="xpn-text-body-md" style={{ color: "var(--xpn-text-on-dark)", fontWeight: 650 }}>
              Ready for review
            </div>
          </div>
          <Button variant="on-dark" size="sm" fullWidth onClick={onLogout}>
            <Icon name="logout" />
            Logout
          </Button>
        </div>
      </aside>

      {sidebarOpen && <div className="xpn-mobile-overlay" onClick={closeSidebar} />}

      <div className="xpn-main-shell">
        <div className="xpn-mobile-bar">
          <div className="xpn-brand" style={{ marginBottom: 0, minHeight: 0, color: "var(--xpn-text)" }}>
            <div className="xpn-brand-mark">X</div>
            <div className="xpn-brand-title">Xpense AI</div>
          </div>
          <Button
            aria-label="Open navigation"
            iconOnly
            variant="secondary"
            size="sm"
            onClick={() => setSidebarOpen(true)}
          >
            <Icon name="menu" />
          </Button>
        </div>

        <main className="xpn-main xpn-dark-app">{children}</main>
        <AssistantDock onLogout={onLogout} />
      </div>
    </div>
  );
}
